// Phase 16.2 — `<BrandMark />`
// Renders a logo image when the brand asset library has one for the given
// tag, otherwise falls back to a clean text wordmark. Replaces the generic
// "green star/circle" as the primary identity per directive.

import React from "react";
import { getLogo, getWordmark } from "@/lib/brand";

export default function BrandMark({
  tag = "logo.latm.primary",
  size = "md",          // "sm" | "md" | "lg"
  variant = "stacked",  // "stacked" | "inline"
  className = "",
  testId,
}) {
  const url = getLogo(tag);
  const { mark, sub } = getWordmark(tag);

  // Width-led sizing — the official Lifted marks are wide graffiti
  // illustrations (rocket + script type). Constraining by width (and letting
  // height auto-fit) preserves their proportions across sidebar/header/hero.
  const sizes = {
    sm: { img: "h-9 max-w-[140px]",   markCls: "text-base",  subCls: "text-[0.55rem]" },
    md: { img: "h-14 max-w-[200px]",  markCls: "text-lg",    subCls: "text-[0.6rem]"  },
    lg: { img: "h-24 max-w-[320px]",  markCls: "text-3xl",   subCls: "text-xs"        },
  }[size] || {};

  if (url) {
    return (
      <img
        src={url}
        alt={`${mark} ${sub}`.trim()}
        className={`${sizes.img} w-auto object-contain ${className}`}
        data-testid={testId || `brandmark-${tag}`}
      />
    );
  }

  if (variant === "inline") {
    return (
      <div
        className={`inline-flex items-baseline gap-1.5 ${className}`}
        data-testid={testId || `brandmark-${tag}`}
      >
        <span className={`font-anton uppercase tracking-[0.18em] text-white ${sizes.markCls}`}>{mark}</span>
        {sub && (
          <span className={`font-bebas uppercase tracking-[0.28em] text-lifted-green ${sizes.subCls}`}>
            {sub}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col leading-none ${className}`}
      data-testid={testId || `brandmark-${tag}`}
    >
      <span className={`font-anton uppercase tracking-[0.18em] text-white ${sizes.markCls}`}>{mark}</span>
      {sub && (
        <span className={`font-bebas uppercase tracking-[0.3em] text-lifted-green ${sizes.subCls} mt-0.5`}>
          {sub}
        </span>
      )}
    </div>
  );
}
